import {
  automationService,
  automationRunService,
  contactService,
  conversationService,
  messageService,
  logService,
  templateService,
  noteService,
} from './dataService';
import type { AutomationFlow, AutomationRun, AutomationNode, Contact, LogLevel } from '../types';
import { generateId } from './dataService';
import { generateReplyReal } from './aiRealService';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentWorkspaceId } from './workspaceState';

// ===================== TYPES =====================

export interface ExecutionLogItem {
  stepId: string;
  nodeId: string;
  actionType: string;
  status: 'success' | 'failed' | 'skipped' | 'pending';
  message: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

interface ExecutionContext {
  automationId: string;
  runId: string;
  contactId: string;
  conversationId: string;
  contact: Contact;
  isTest: boolean;
  variables: Record<string, any>;
  log: ExecutionLogItem[];
  stopped: boolean;
  failed: boolean;
  errorMessage: string;
}

// ===================== TRIGGER TYPES =====================

export const TRIGGER_TYPES = [
  { value: 'new_contact_created', label: 'New Contact Created', icon: 'UserPlus', color: 'border-blue-500' },
  { value: 'conversation_created', label: 'Conversation Created', icon: 'MessageSquare', color: 'border-emerald-500' },
  { value: 'incoming_message_received', label: 'Incoming Message Received', icon: 'MessageSquare', color: 'border-green-500' },
  { value: 'tag_added', label: 'Tag Added', icon: 'Tag', color: 'border-indigo-500' },
  { value: 'tag_removed', label: 'Tag Removed', icon: 'Tag', color: 'border-violet-500' },
  { value: 'order_status_changed', label: 'Order Status Changed', icon: 'ShoppingBag', color: 'border-orange-500' },
  { value: 'delivery_status_changed', label: 'Delivery Status Changed', icon: 'ShoppingBag', color: 'border-amber-500' },
  { value: 'cod_confirmation_changed', label: 'COD Confirmation Changed', icon: 'ShoppingBag', color: 'border-yellow-500' },
  { value: 'contact_updated', label: 'Contact Updated', icon: 'UserPlus', color: 'border-cyan-500' },
  { value: 'campaign_finished', label: 'Campaign Finished', icon: 'Zap', color: 'border-purple-500' },
  { value: 'manual_test_trigger', label: 'Manual / Test', icon: 'Zap', color: 'border-slate-500' },
] as const;

// ===================== ACTION TYPES =====================

export const ACTION_TYPES = [
  { value: 'send_message', label: 'Send Message', icon: 'MessageSquare', color: 'border-emerald-500', category: 'messaging' },
  { value: 'send_template', label: 'Send Template', icon: 'FileText', color: 'border-teal-500', category: 'messaging' },
  { value: 'add_tag', label: 'Add Tag', icon: 'Tag', color: 'border-blue-500', category: 'contact' },
  { value: 'remove_tag', label: 'Remove Tag', icon: 'Tag', color: 'border-indigo-500', category: 'contact' },
  { value: 'update_contact_field', label: 'Update Contact Field', icon: 'UserPlus', color: 'border-violet-500', category: 'contact' },
  { value: 'update_order_status', label: 'Update Order Status', icon: 'ShoppingBag', color: 'border-orange-500', category: 'ecommerce' },
  { value: 'update_delivery_status', label: 'Update Delivery Status', icon: 'ShoppingBag', color: 'border-amber-500', category: 'ecommerce' },
  { value: 'update_cod_confirmation', label: 'Update COD Confirmation', icon: 'ShoppingBag', color: 'border-yellow-500', category: 'ecommerce' },
  { value: 'update_conversation_status', label: 'Update Conversation Status', icon: 'MessageSquare', color: 'border-cyan-500', category: 'conversation' },
  { value: 'assign_agent', label: 'Assign Agent', icon: 'UserPlus', color: 'border-purple-500', category: 'conversation' },
  { value: 'create_note', label: 'Create Note', icon: 'FileText', color: 'border-slate-500', category: 'contact' },
  { value: 'wait_delay', label: 'Wait / Delay', icon: 'Clock', color: 'border-amber-500', category: 'flow' },
  { value: 'if_else_condition', label: 'If/Else Condition', icon: 'GitBranch', color: 'border-rose-500', category: 'flow' },
  { value: 'stop_flow', label: 'Stop Flow', icon: 'Zap', color: 'border-red-500', category: 'flow' },
  { value: 'write_log', label: 'Write Log', icon: 'FileText', color: 'border-gray-500', category: 'system' },
  { value: 'ai_reply', label: 'AI Reply', icon: 'Bot', color: 'border-purple-500', category: 'messaging' },
] as const;

// ===================== CONDITION FIELDS =====================

export const CONDITION_FIELDS = [
  { value: 'leadStatus', label: 'Lead Status', type: 'select', options: ['new', 'lead', 'qualified', 'customer', 'lost'] },
  { value: 'city', label: 'City', type: 'text' },
  { value: 'country', label: 'Country', type: 'text' },
  { value: 'tags', label: 'Has Tag', type: 'text' },
  { value: 'orderStatus', label: 'Order Status', type: 'select', options: ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'] },
  { value: 'deliveryStatus', label: 'Delivery Status', type: 'text' },
  { value: 'codConfirmationStatus', label: 'COD Confirmation', type: 'text' },
  { value: 'assignedAgentId', label: 'Assigned Agent', type: 'text' },
  { value: 'conversationStatus', label: 'Conversation Status', type: 'select', options: ['open', 'pending', 'closed'] },
  { value: 'language', label: 'Language', type: 'text' },
] as const;

export const CONDITION_OPERATORS = ['equals', 'not_equals', 'contains', 'not_contains', 'exists', 'not_exists'] as const;

// ===================== DELAY SCHEDULER =====================

function getDelayStorageKey(): string {
  const wsId = getCurrentWorkspaceId();
  return `wf_automation_delayed_runs_${wsId}`;
}

interface DelayedRun {
  id: string;
  automationId: string;
  runId: string;
  contactId: string;
  conversationId: string;
  resumeNodeId: string;
  resumeAt: string;
  isTest: boolean;
  createdAt: string;
}

function getDelayedRuns(): DelayedRun[] {
  try {
    return JSON.parse(localStorage.getItem(getDelayStorageKey()) || '[]');
  } catch {
    return [];
  }
}

function saveDelayedRuns(runs: DelayedRun[]) {
  localStorage.setItem(getDelayStorageKey(), JSON.stringify(runs));
}

function addDelayedRun(run: DelayedRun) {
  const runs = getDelayedRuns();
  runs.push(run);
  saveDelayedRuns(runs);
}

function removeDelayedRun(id: string) {
  saveDelayedRuns(getDelayedRuns().filter((r) => r.id !== id));
}

function serializeExecutionLog(log: ExecutionLogItem[]) {
  return log.map((item) => ({
    nodeId: item.nodeId,
    action: item.actionType,
    result: item.message,
    timestamp: item.timestamp,
  }));
}

function getConversationHistory(conversationId: string) {
  const allMsgs = messageService.getByConversation(conversationId);
  const byWamid = new Map<string, { direction: string; body: string }>();
  allMsgs.forEach((m) => {
    if (m.externalMessageId) byWamid.set(m.externalMessageId, { direction: m.direction, body: m.body });
  });
  return allMsgs.slice(-10).map((message) => {
    const replyTo = message.contextWamid ? byWamid.get(message.contextWamid) : undefined;
    return {
      direction: message.direction,
      body: message.body,
      replyTo: replyTo ? { direction: replyTo.direction, body: replyTo.body } : null,
    };
  });
}

function getLastInboundMessage(ctx: ExecutionContext): string {
  const explicitMessage = ctx.variables._lastInboundMessage;
  if (typeof explicitMessage === 'string' && explicitMessage.trim()) return explicitMessage;

  const messages = messageService.getByConversation(ctx.conversationId);
  const lastInbound = [...messages].reverse().find((message) => message.direction === 'inbound');
  return lastInbound?.body || 'Hello';
}

function getOrderContext(contact: Contact) {
  return {
    orderStatus: contact.orderStatus,
    deliveryStatus: contact.deliveryStatus,
    productName: contact.productName,
    orderAmount: contact.orderAmount,
  };
}

function shortenAiResponse(reply: string, maxLength: string) {
  if (maxLength !== 'short') return reply;
  const sentences = reply.split(/(?<=[.!?])\s+/).filter(Boolean);
  return sentences.slice(0, 2).join(' ').trim() || reply;
}

// Check and resume delayed runs - called on init and periodically
export async function processDelayedRuns() {
  const now = new Date().getTime();
  const delayed = getDelayedRuns();
  const due = delayed.filter((d) => new Date(d.resumeAt).getTime() <= now);

  for (const delayedRun of due) {
    const flow = automationService.getById(delayedRun.automationId);
    if (!flow || flow.status !== 'active') {
      removeDelayedRun(delayedRun.id);
      continue;
    }

    const contact = contactService.getById(delayedRun.contactId);
    if (!contact) {
      removeDelayedRun(delayedRun.id);
      continue;
    }

    logService.add('automations', 'delay_resumed', `Delayed run resumed for "${flow.name}"`, {
      automationId: delayedRun.automationId,
      runId: delayedRun.runId,
      contactId: delayedRun.contactId,
    }, 'info', 'automation_run', delayedRun.runId);

    await resumeExecution(
      flow,
      delayedRun.runId,
      delayedRun.contactId,
      delayedRun.conversationId,
      delayedRun.resumeNodeId,
      delayedRun.isTest,
    );

    removeDelayedRun(delayedRun.id);
  }
}

// Start periodic check
let delayInterval: ReturnType<typeof setInterval> | null = null;
export function startDelayScheduler() {
  if (delayInterval) return;
  delayInterval = setInterval(() => {
    void processDelayedRuns();
  }, 15000);
  void processDelayedRuns();
}

export function stopDelayScheduler() {
  if (delayInterval) {
    clearInterval(delayInterval);
    delayInterval = null;
  }
}

// ===================== TRIGGER ENTRY POINT =====================

export function triggerAutomations(triggerType: string, context: {
  contactId: string;
  conversationId?: string;
  metadata?: Record<string, any>;
}) {
  const flows = automationService.getByTrigger(triggerType);
  flows.forEach((flow) => {
    if (!matchesTriggerConfig(flow, triggerType, context.metadata)) return;
    void executeAutomation(flow, context.contactId, context.conversationId, triggerType, false);
  });
}

function matchesTriggerConfig(flow: AutomationFlow, triggerType: string, metadata?: Record<string, any>): boolean {
  const triggerNode = flow.nodes.find((n) => n.type === 'Trigger');
  if (!triggerNode?.config) return true;

  if (triggerType === 'tag_added' && triggerNode.config.triggerTag) {
    return metadata?.tag === triggerNode.config.triggerTag;
  }

  if (triggerType === 'order_status_changed' && triggerNode.config.triggerOrderStatus) {
    return metadata?.orderStatus === triggerNode.config.triggerOrderStatus;
  }

  return true;
}

// ===================== MAIN EXECUTION =====================

export async function executeAutomation(
  flow: AutomationFlow,
  contactId: string,
  conversationId?: string,
  triggerSource: string = 'manual',
  isTest: boolean = false,
): Promise<AutomationRun | null> {
  const contact = contactService.getById(contactId);
  if (!contact) {
    logService.add('automations', 'run_skipped', `Automation "${flow.name}" skipped: contact not found`, {
      automationId: flow.id,
      contactId,
    }, 'warn', 'automation', flow.id);
    return null;
  }

  const convId = conversationId || conversationService.getOrCreateForContact(contactId);
  const now = new Date().toISOString();

  const runId = automationRunService.create({
    automationId: flow.id,
    automationName: flow.name,
    contactId,
    conversationId: convId,
    triggerSource,
    status: 'running',
    isTest,
    startedAt: now,
    executionLog: [],
  });

  logService.add('automations', 'triggered', `Automation "${flow.name}" triggered for ${contact.fullName}`, {
    automationId: flow.id,
    runId,
    contactId,
    triggerSource,
    isTest,
  }, 'info', 'automation_run', runId);

  const ctx: ExecutionContext = {
    automationId: flow.id,
    runId,
    contactId,
    conversationId: convId,
    contact: { ...contact },
    isTest,
    variables: {},
    log: [],
    stopped: false,
    failed: false,
    errorMessage: '',
  };

  const initialInboundMessage = getLastInboundMessage(ctx);
  if (initialInboundMessage) {
    ctx.variables._lastInboundMessage = initialInboundMessage;
  }

  const triggerNode = flow.nodes.find((n) => n.type === 'Trigger');
  if (triggerNode) {
    ctx.log.push({
      stepId: generateId(),
      nodeId: triggerNode.id,
      actionType: 'trigger',
      status: 'success',
      message: `Trigger fired: ${triggerNode.label}`,
      timestamp: new Date().toISOString(),
    });
  }

  const startNodeId = triggerNode?.id;
  if (startNodeId) {
    await executeFromNode(flow, startNodeId, ctx);
  }

  if (!ctx.stopped || ctx.failed) {
    const finishedAt = new Date().toISOString();
    const status = ctx.failed ? 'failed' : 'completed';

    automationRunService.update(runId, {
      status,
      finishedAt,
      errorMessage: ctx.failed ? ctx.errorMessage : undefined,
      executionLog: serializeExecutionLog(ctx.log),
    });

    automationService.update(flow.id, { lastRunAt: finishedAt });

    if (ctx.failed) {
      logService.add('automations', 'run_failed', `Automation "${flow.name}" failed: ${ctx.errorMessage}`, {
        automationId: flow.id,
        runId,
        contactId,
        isTest,
      }, 'error', 'automation_run', runId);
    } else {
      logService.add('automations', 'run_completed', `Automation "${flow.name}" completed for ${contact.fullName}`, {
        automationId: flow.id,
        runId,
        contactId,
        isTest,
        steps: ctx.log.length,
      }, 'success', 'automation_run', runId);
    }

    if (!isTest) {
      messageService.sendSystemMessage(
        convId,
        contactId,
        `⚡ Automation "${flow.name}" ${ctx.failed ? 'failed' : 'completed'}`,
        flow.id,
      );
    }
  }

  return automationRunService.getAll().find((run) => run.id === runId) || null;
}

// ===================== GRAPH TRAVERSAL =====================

function getNextNodes(flow: AutomationFlow, nodeId: string): string[] {
  return (flow.edges || []).filter((edge) => edge.source === nodeId).map((edge) => edge.target);
}

function getNodeById(flow: AutomationFlow, nodeId: string): AutomationNode | undefined {
  return flow.nodes.find((node) => node.id === nodeId);
}

async function executeFromNode(flow: AutomationFlow, nodeId: string, ctx: ExecutionContext) {
  if (ctx.stopped || ctx.failed) return;

  const nextIds = getNextNodes(flow, nodeId);
  for (const nextId of nextIds) {
    if (ctx.stopped || ctx.failed) break;

    const node = getNodeById(flow, nextId);
    if (!node) continue;

    try {
      const result = await executeNode(node, flow, ctx);

      ctx.log.push({
        stepId: generateId(),
        nodeId: node.id,
        actionType: node.config?.actionType || node.type,
        status: result.success ? 'success' : (result.delayed ? 'pending' : 'failed'),
        message: result.message,
        timestamp: new Date().toISOString(),
        metadata: result.metadata,
      });

      if (result.delayed) {
        return;
      }

      if (!result.success) {
        ctx.failed = true;
        ctx.errorMessage = result.message;
        break;
      }

      if (result.stopped) {
        ctx.stopped = true;
        break;
      }

      if (result.branchTarget) {
        await executeFromNode(flow, result.branchTarget, ctx);
      } else {
        await executeFromNode(flow, nextId, ctx);
      }
    } catch (err: any) {
      ctx.failed = true;
      ctx.errorMessage = err?.message || 'Unknown error';
      ctx.log.push({
        stepId: generateId(),
        nodeId: node.id,
        actionType: node.config?.actionType || node.type,
        status: 'failed',
        message: `Error: ${ctx.errorMessage}`,
        timestamp: new Date().toISOString(),
      });
      break;
    }
  }
}

// ===================== RESUME AFTER DELAY =====================

async function resumeExecution(
  flow: AutomationFlow,
  runId: string,
  contactId: string,
  conversationId: string,
  resumeFromNodeId: string,
  isTest: boolean,
) {
  const contact = contactService.getById(contactId);
  if (!contact) return;

  const existingRun = automationRunService.getAll().find((run) => run.id === runId);
  const previousLog = existingRun?.executionLog?.map((logItem) => ({
    stepId: generateId(),
    nodeId: logItem.nodeId,
    actionType: logItem.action,
    status: 'success' as const,
    message: logItem.result,
    timestamp: logItem.timestamp,
  })) || [];

  const ctx: ExecutionContext = {
    automationId: flow.id,
    runId,
    contactId,
    conversationId,
    contact: { ...contact },
    isTest,
    variables: {},
    log: [...previousLog],
    stopped: false,
    failed: false,
    errorMessage: '',
  };

  ctx.variables._lastInboundMessage = getLastInboundMessage(ctx);

  ctx.log.push({
    stepId: generateId(),
    nodeId: resumeFromNodeId,
    actionType: 'delay_resumed',
    status: 'success',
    message: 'Delay completed, execution resumed',
    timestamp: new Date().toISOString(),
  });

  await executeFromNode(flow, resumeFromNodeId, ctx);

  const finishedAt = new Date().toISOString();
  automationRunService.update(runId, {
    status: ctx.failed ? 'failed' : 'completed',
    finishedAt,
    errorMessage: ctx.failed ? ctx.errorMessage : undefined,
    executionLog: serializeExecutionLog(ctx.log),
  });

  automationService.update(flow.id, { lastRunAt: finishedAt });
}

// ===================== NODE EXECUTION =====================

interface NodeResult {
  success: boolean;
  message: string;
  stopped?: boolean;
  delayed?: boolean;
  branchTarget?: string;
  metadata?: Record<string, any>;
}

async function executeNode(node: AutomationNode, flow: AutomationFlow, ctx: ExecutionContext): Promise<NodeResult> {
  const actionType = node.config?.actionType || inferActionType(node);

  switch (actionType) {
    case 'send_message': return execSendMessage(node, ctx);
    case 'send_template': return execSendTemplate(node, ctx);
    case 'add_tag': return execAddTag(node, ctx);
    case 'remove_tag': return execRemoveTag(node, ctx);
    case 'update_contact_field': return execUpdateContactField(node, ctx);
    case 'update_order_status': return execUpdateOrderStatus(node, ctx);
    case 'update_delivery_status': return execUpdateDeliveryStatus(node, ctx);
    case 'update_cod_confirmation': return execUpdateCodConfirmation(node, ctx);
    case 'update_conversation_status': return execUpdateConversationStatus(node, ctx);
    case 'assign_agent': return execAssignAgent(node, ctx);
    case 'create_note': return execCreateNote(node, ctx);
    case 'wait_delay': return execWaitDelay(node, flow, ctx);
    case 'if_else_condition': return execCondition(node, flow, ctx);
    case 'stop_flow': return { success: true, message: 'Flow stopped', stopped: true };
    case 'write_log': return execWriteLog(node, ctx);
    case 'ai_reply': return execAiReply(node, ctx);
    default: return { success: true, message: `Action "${node.label}" executed (generic)` };
  }
}

function inferActionType(node: AutomationNode): string {
  const label = (node.label || '').toLowerCase();
  if (label.includes('send') && label.includes('template')) return 'send_template';
  if (label.includes('send') && label.includes('message')) return 'send_message';
  if (label.includes('wait') || label.includes('delay')) return 'wait_delay';
  if (label.includes('add') && label.includes('tag')) return 'add_tag';
  if (label.includes('remove') && label.includes('tag')) return 'remove_tag';
  if (label.includes('assign')) return 'assign_agent';
  if (label.includes('condition') || label.includes('filter') || label.includes('if')) return 'if_else_condition';
  if (label.includes('stop')) return 'stop_flow';
  if (label.includes('note')) return 'create_note';
  if (label.includes('order') && label.includes('status')) return 'update_order_status';
  if (label.includes('delivery')) return 'update_delivery_status';
  if (label.includes('cod')) return 'update_cod_confirmation';
  if (label.includes('conversation') && label.includes('status')) return 'update_conversation_status';
  if (label.includes('log')) return 'write_log';
  return 'generic';
}

// ===================== ACTION HANDLERS =====================

async function execSendMessage(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const text = node.config?.message || `Hello ${ctx.contact.fullName}, this is an automated message.`;
  if (!text.trim()) return { success: false, message: 'Message text is empty' };

  if (ctx.isTest) {
    return { success: true, message: `Message sent (test): "${text.substring(0, 60)}..."` };
  }

  try {
    const { sendWhatsAppMessage } = await import('./whatsappService');
    const sendResult = await sendWhatsAppMessage(ctx.conversationId, ctx.contactId, text, 'automation', { automationId: ctx.automationId });

    if (!sendResult.success) {
      logService.add('automations', 'whatsapp_message_failed', `Send failed: ${sendResult.error}`, {
        automationId: ctx.automationId, contactId: ctx.contactId,
      }, 'error', 'message', ctx.runId);
      return { success: false, message: `Message send failed: ${sendResult.error}` };
    }

    // Persist to DB
    try {
      await supabase.from('messages').upsert({
        id: sendResult.localMessageId,
        workspace_id: getCurrentWorkspaceId(),
        conversation_id: ctx.conversationId, contact_id: ctx.contactId,
        direction: 'outbound', message_type: 'text', body: text,
        delivery_state: sendResult.deliveryState || 'sent',
        external_message_id: sendResult.messageId || null,
        automation_id: ctx.automationId, sent_by: 'automation',
      });
      await supabase.from('conversations').update({
        last_message_preview: text.substring(0, 100),
        last_message_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      }).eq('id', ctx.conversationId);
      await supabase.from('contacts').update({
        last_message_at: new Date().toISOString(), last_contact_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', ctx.contactId);
    } catch (dbErr) {
      console.warn('[AutomationEngine] DB persist send_message failed:', dbErr);
    }

    logService.add('automations', 'whatsapp_message_sent', `Automation sent message to ${ctx.contact.fullName}`, {
      automationId: ctx.automationId, contactId: ctx.contactId, wamid: sendResult.messageId,
    }, 'info', 'message', ctx.runId);

    return { success: true, message: `Message sent: "${text.substring(0, 60)}..."`, metadata: { wamid: sendResult.messageId } };
  } catch (err: any) {
    // Fallback: create local message
    messageService.create({
      conversationId: ctx.conversationId, contactId: ctx.contactId,
      direction: 'outbound', messageType: 'text', body: text,
      deliveryState: 'local_only', sentBy: 'automation', automationId: ctx.automationId,
    });
    logService.add('automations', 'message_sent_local', `Message sent locally (WA unavailable): ${err?.message}`, {
      automationId: ctx.automationId, contactId: ctx.contactId,
    }, 'warn', 'message', ctx.runId);
    return { success: true, message: `Message sent (local): "${text.substring(0, 60)}..."` };
  }
}

async function execSendTemplate(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const templateId = node.config?.templateId;
  if (!templateId) return { success: false, message: 'No template configured' };

  const template = templateService.getById(templateId);
  if (!template) return { success: false, message: `Template "${templateId}" not found` };
  if (template.status !== 'approved') {
    return { success: false, message: `Template "${template.name}" is ${template.status}. Only approved templates can be sent.` };
  }

  let body = template.bodyText;
  const bodyParams: string[] = [];
  (template.variables || []).forEach((variable, index) => {
    const placeholder = `{{${index + 1}}}`;
    const contactValue = (ctx.contact as any)[variable.key] || variable.fallbackValue || placeholder;
    body = body.replace(new RegExp(`\\{\\{${index + 1}\\}\\}`, 'g'), contactValue);
    bodyParams.push(contactValue);
  });

  if (ctx.isTest) {
    return { success: true, message: `Template "${template.name}" sent (test)`, metadata: { templateId, templateName: template.name } };
  }

  try {
    const { sendWhatsAppTemplate } = await import('./whatsappService');
    const result = await sendWhatsAppTemplate(
      ctx.conversationId, ctx.contactId,
      template.metaTemplateName || template.name,
      body, templateId, undefined,
      template.language || 'en',
      bodyParams.length > 0 ? { bodyParams } : undefined,
    );

    if (result.success) {
      // Persist to DB
      try {
        await supabase.from('messages').upsert({
          id: result.localMessageId || generateId(),
          workspace_id: getCurrentWorkspaceId(),
          conversation_id: ctx.conversationId, contact_id: ctx.contactId,
          direction: 'outbound', message_type: 'template',
          body: body || `[Template: ${template.name}]`,
          delivery_state: 'sent', external_message_id: result.messageId || null,
          template_id: templateId, automation_id: ctx.automationId, sent_by: 'automation',
        });
        await supabase.from('conversations').update({
          last_message_preview: `[Template: ${template.name}]`,
          last_message_at: new Date().toISOString(), updated_at: new Date().toISOString(),
        }).eq('id', ctx.conversationId);
        await supabase.from('contacts').update({
          last_message_at: new Date().toISOString(), last_contact_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }).eq('id', ctx.contactId);
      } catch (dbErr) {
        console.warn('[AutomationEngine] DB persist send_template failed:', dbErr);
      }

      logService.add('automations', 'template_sent_success', `Template "${template.name}" sent to ${ctx.contact.fullName}`, {
        automationId: ctx.automationId, templateId, contactId: ctx.contactId, wamid: result.messageId,
      }, 'success', 'message', ctx.runId);

      return { success: true, message: `Template "${template.name}" sent`, metadata: { templateId, templateName: template.name, wamid: result.messageId } };
    } else {
      logService.add('automations', 'template_send_failed', `Template "${template.name}" failed: ${result.error}`, {
        automationId: ctx.automationId, templateId, contactId: ctx.contactId, error: result.error,
      }, 'error', 'message', ctx.runId);
      return { success: false, message: `Template send failed: ${result.error}` };
    }
  } catch (err: any) {
    logService.add('automations', 'template_send_failed', `Template error: ${err.message}`, {
      automationId: ctx.automationId, templateId, contactId: ctx.contactId,
    }, 'error', 'message', ctx.runId);
    return { success: false, message: `Template send error: ${err.message}` };
  }
}

async function execAddTag(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const tag = node.config?.tag;
  if (!tag) return { success: false, message: 'No tag configured' };

  const tags = [...new Set([...(ctx.contact.tags || []), tag])];
  if (!ctx.isTest) {
    contactService.update(ctx.contactId, { tags });
    try {
      await supabase.from('contacts').update({ tags, updated_at: new Date().toISOString() }).eq('id', ctx.contactId);
    } catch {}
    logService.add('automations', 'contact_tag_added', `Tag "${tag}" added`, { tag, contactId: ctx.contactId }, 'info', 'contact', ctx.contactId);
  }
  ctx.contact.tags = tags;
  return { success: true, message: `Tag "${tag}" added` };
}

async function execRemoveTag(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const tag = node.config?.tag;
  if (!tag) return { success: false, message: 'No tag configured' };

  const tags = (ctx.contact.tags || []).filter((item) => item !== tag);
  if (!ctx.isTest) {
    contactService.update(ctx.contactId, { tags });
    try {
      await supabase.from('contacts').update({ tags, updated_at: new Date().toISOString() }).eq('id', ctx.contactId);
    } catch {}
    logService.add('automations', 'contact_tag_removed', `Tag "${tag}" removed`, { tag, contactId: ctx.contactId }, 'info', 'contact', ctx.contactId);
  }
  ctx.contact.tags = tags;
  return { success: true, message: `Tag "${tag}" removed` };
}

async function execUpdateContactField(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const field = node.config?.field;
  const value = node.config?.value;
  if (!field) return { success: false, message: 'No field configured' };

  if (!ctx.isTest) {
    contactService.update(ctx.contactId, { [field]: value });
    (ctx.contact as any)[field] = value;
    try {
      const snakeField = field.replace(/([A-Z])/g, '_$1').toLowerCase();
      await supabase.from('contacts').update({ [snakeField]: value, updated_at: new Date().toISOString() } as any).eq('id', ctx.contactId);
    } catch {}
    logService.add('automations', 'contact_updated', `Field "${field}" → "${value}"`, { field, value, contactId: ctx.contactId }, 'info', 'contact', ctx.contactId);
  }
  return { success: true, message: `Contact field "${field}" set to "${value}"` };
}

async function execUpdateOrderStatus(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const status = node.config?.orderStatus;
  if (!status) return { success: false, message: 'No order status configured' };

  if (!ctx.isTest) {
    const now = new Date().toISOString();
    contactService.update(ctx.contactId, { orderStatus: status, lastOrderUpdateAt: now });
    try {
      await supabase.from('contacts').update({ order_status: status, last_order_update_at: now, updated_at: now }).eq('id', ctx.contactId);
    } catch {}
    logService.add('automations', 'order_status_changed', `Order status → "${status}"`, { status, contactId: ctx.contactId }, 'info', 'contact', ctx.contactId);
  }
  return { success: true, message: `Order status updated to "${status}"` };
}

async function execUpdateDeliveryStatus(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const status = node.config?.deliveryStatus;
  if (!status) return { success: false, message: 'No delivery status configured' };

  if (!ctx.isTest) {
    const now = new Date().toISOString();
    contactService.update(ctx.contactId, { deliveryStatus: status, lastOrderUpdateAt: now });
    try {
      await supabase.from('contacts').update({ delivery_status: status, last_order_update_at: now, updated_at: now }).eq('id', ctx.contactId);
    } catch {}
    logService.add('automations', 'delivery_status_changed', `Delivery → "${status}"`, { status, contactId: ctx.contactId }, 'info', 'contact', ctx.contactId);
  }
  return { success: true, message: `Delivery status updated to "${status}"` };
}

async function execUpdateCodConfirmation(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const status = node.config?.codConfirmationStatus;
  if (!status) return { success: false, message: 'No COD status configured' };

  if (!ctx.isTest) {
    const now = new Date().toISOString();
    contactService.update(ctx.contactId, { codConfirmationStatus: status, lastOrderUpdateAt: now });
    try {
      await supabase.from('contacts').update({ cod_confirmation_status: status, last_order_update_at: now, updated_at: now }).eq('id', ctx.contactId);
    } catch {}
    logService.add('automations', 'cod_status_changed', `COD → "${status}"`, { status, contactId: ctx.contactId }, 'info', 'contact', ctx.contactId);
  }
  return { success: true, message: `COD confirmation updated to "${status}"` };
}

async function execUpdateConversationStatus(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const status = node.config?.conversationStatus;
  if (!status) return { success: false, message: 'No conversation status configured' };

  if (!ctx.isTest) {
    conversationService.update(ctx.conversationId, { status });
    try {
      await supabase.from('conversations').update({ status, updated_at: new Date().toISOString() }).eq('id', ctx.conversationId);
    } catch {}
    logService.add('automations', 'conversation_status_changed', `Status → "${status}"`, { status, conversationId: ctx.conversationId }, 'info', 'conversation', ctx.conversationId);
  }
  return { success: true, message: `Conversation status changed to "${status}"` };
}

async function execAssignAgent(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const agentId = node.config?.agentId;
  if (!agentId) return { success: false, message: 'No agent configured' };

  if (!ctx.isTest) {
    const now = new Date().toISOString();
    contactService.update(ctx.contactId, { assignedAgentId: agentId });
    conversationService.update(ctx.conversationId, { assignedAgentId: agentId });
    try {
      await supabase.from('contacts').update({ assigned_agent_id: agentId, updated_at: now }).eq('id', ctx.contactId);
      await supabase.from('conversations').update({ assigned_agent_id: agentId, updated_at: now }).eq('id', ctx.conversationId);
    } catch {}
    logService.add('automations', 'conversation_assigned', `Agent "${agentId}" assigned`, { agentId, contactId: ctx.contactId, conversationId: ctx.conversationId }, 'info', 'conversation', ctx.conversationId);
  }
  return { success: true, message: `Agent "${agentId}" assigned` };
}

async function execCreateNote(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const body = node.config?.noteBody || `Automation note for ${ctx.contact.fullName}`;
  if (!body.trim()) return { success: false, message: 'Note body is empty' };

  if (!ctx.isTest) {
    const noteId = generateId();
    noteService.create(ctx.contactId, body, 'automation');
    try {
      await supabase.from('contact_notes').insert({ id: noteId, contact_id: ctx.contactId, body, created_by: 'automation', workspace_id: getCurrentWorkspaceId() });
      const { data: c } = await supabase.from('contacts').select('notes_count').eq('id', ctx.contactId).maybeSingle();
      await supabase.from('contacts').update({ notes_count: (c?.notes_count || 0) + 1, updated_at: new Date().toISOString() }).eq('id', ctx.contactId);
    } catch {}
    logService.add('automations', 'note_added', `Note added to contact`, { noteBody: body.substring(0, 200), contactId: ctx.contactId }, 'info', 'contact', ctx.contactId);
  }
  return { success: true, message: `Note created: "${body.substring(0, 50)}..."` };
}

async function execWriteLog(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  const msg = node.config?.logMessage || 'Automation log entry';
  const level = (node.config?.logLevel || 'info') as LogLevel;

  logService.add('automations', 'automation_log_written', msg, {
    automationId: ctx.automationId, contactId: ctx.contactId, customLog: true,
  }, level, 'automation_run', ctx.runId);

  try {
    await supabase.from('system_logs').insert({
      id: generateId(), module: 'automation', action: 'automation_log_written',
      level, message: msg,
      entity_type: 'automation_run', entity_id: ctx.runId,
      metadata: { automationId: ctx.automationId, contactId: ctx.contactId, conversationId: ctx.conversationId, customLog: true },
      workspace_id: getCurrentWorkspaceId(),
    });
  } catch {}

  return { success: true, message: `Log written: "${msg}"` };
}

function execWaitDelay(node: AutomationNode, flow: AutomationFlow, ctx: ExecutionContext): NodeResult {
  const amount = node.config?.delayAmount || 1;
  const unit = node.config?.delayUnit || 'minutes';

  let delayMs = amount * 60 * 1000;
  if (unit === 'hours') delayMs = amount * 60 * 60 * 1000;
  if (unit === 'days') delayMs = amount * 24 * 60 * 60 * 1000;
  if (unit === 'seconds') delayMs = amount * 1000;

  if (ctx.isTest || delayMs < 30000) {
    return { success: true, message: `Delay ${amount} ${unit} (simulated instantly for ${ctx.isTest ? 'test' : 'short delay'})` };
  }

  const resumeAt = new Date(Date.now() + delayMs).toISOString();
  addDelayedRun({
    id: generateId(),
    automationId: ctx.automationId,
    runId: ctx.runId,
    contactId: ctx.contactId,
    conversationId: ctx.conversationId,
    resumeNodeId: node.id,
    resumeAt,
    isTest: ctx.isTest,
    createdAt: new Date().toISOString(),
  });

  automationRunService.update(ctx.runId, {
    status: 'running',
    executionLog: serializeExecutionLog(ctx.log),
  });

  logService.add('automations', 'delay_scheduled', `Delay ${amount} ${unit} scheduled, will resume at ${resumeAt}`, {
    automationId: ctx.automationId,
    runId: ctx.runId,
    resumeAt,
  }, 'info', 'automation_run', ctx.runId);

  return {
    success: true,
    message: `Delay ${amount} ${unit} scheduled (resumes at ${new Date(resumeAt).toLocaleTimeString()})`,
    delayed: true,
  };
}

function execCondition(node: AutomationNode, flow: AutomationFlow, ctx: ExecutionContext): NodeResult {
  const field = node.config?.conditionField;
  const operator = node.config?.conditionOperator || 'equals';
  const value = node.config?.conditionValue;

  if (!field) return { success: true, message: 'Condition: no field configured, passing through' };

  let actualValue: any;
  if (field === 'conversationStatus') {
    const conversation = conversationService.getById(ctx.conversationId);
    actualValue = conversation?.status;
  } else if (field === 'tags') {
    actualValue = ctx.contact.tags;
  } else {
    actualValue = (ctx.contact as any)[field];
  }

  let conditionMet = false;
  switch (operator) {
    case 'equals':
      conditionMet = String(actualValue) === String(value);
      break;
    case 'not_equals':
      conditionMet = String(actualValue) !== String(value);
      break;
    case 'contains':
      if (Array.isArray(actualValue)) conditionMet = actualValue.includes(value);
      else conditionMet = String(actualValue || '').includes(String(value));
      break;
    case 'not_contains':
      if (Array.isArray(actualValue)) conditionMet = !actualValue.includes(value);
      else conditionMet = !String(actualValue || '').includes(String(value));
      break;
    case 'exists':
      conditionMet = actualValue !== undefined && actualValue !== null && actualValue !== '';
      break;
    case 'not_exists':
      conditionMet = actualValue === undefined || actualValue === null || actualValue === '';
      break;
  }

  const edges = (flow.edges || []).filter((edge) => edge.source === node.id);
  const trueBranch = edges.find((edge) => (edge as any).label === 'true' || (edge as any).branch === 'true');
  const falseBranch = edges.find((edge) => (edge as any).label === 'false' || (edge as any).branch === 'false');
  const targetEdge = conditionMet ? (trueBranch || edges[0]) : (falseBranch || edges[1]);

  return {
    success: true,
    message: `Condition ${field} ${operator} "${value}": ${conditionMet ? 'TRUE' : 'FALSE'} (actual: "${actualValue}")`,
    branchTarget: targetEdge?.target,
    metadata: { field, operator, value, actualValue, conditionMet },
  };
}

// ===================== AI REPLY HANDLER =====================

async function execAiReply(node: AutomationNode, ctx: ExecutionContext): Promise<NodeResult> {
  try {
    // ─── Per-conversation AI pause check ───
    if (ctx.conversationId && !ctx.isTest) {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: convPause } = await supabase
          .from('conversations')
          .select('ai_paused, ai_paused_until')
          .eq('id', ctx.conversationId)
          .maybeSingle();
        const cp = convPause as any;
        let isPaused = !!cp?.ai_paused;
        if (isPaused && cp?.ai_paused_until && new Date(cp.ai_paused_until).getTime() <= Date.now()) {
          isPaused = false;
          await supabase.from('conversations')
            .update({ ai_paused: false, ai_paused_until: null } as any)
            .eq('id', ctx.conversationId);
        }
        if (isPaused) {
          logService.add('automations', 'automation_ai_reply_skipped_paused',
            `AI reply skipped — conversation paused`, {
              automationId: ctx.automationId,
              contactId: ctx.contactId,
              conversationId: ctx.conversationId,
              pausedUntil: cp?.ai_paused_until || 'indefinite',
            });
          return { success: true, message: 'AI reply skipped (conversation paused)' };
        }
      } catch (e) {
        console.warn('[AutomationEngine] AI pause check failed, proceeding', e);
      }
    }

    const tone = node.config?.aiTone || 'professional';
    const maxLength = node.config?.aiMaxLength || 'medium';
    const autoSend = node.config?.aiAutoSend !== false;
    const saveField = node.config?.aiSaveField || '';
    const aiPrompt = node.config?.aiPrompt || '';
    const aiContext = node.config?.aiContext || '';
    const contactName = ctx.contact.fullName || 'Customer';
    const lastMsg = getLastInboundMessage(ctx);
    const conversationHistory = getConversationHistory(ctx.conversationId);

    ctx.variables._lastInboundMessage = lastMsg;

    logService.add('automations', 'automation_ai_called', `Automation AI step started for ${contactName}`, {
      automationId: ctx.automationId,
      contactId: ctx.contactId,
      runId: ctx.runId,
      prompt: aiPrompt,
      context: aiContext,
      lastInboundMessage: lastMsg,
      tone,
      autoSend,
      isTest: ctx.isTest,
    }, 'info', 'automation_run', ctx.runId);

    const aiResult = await generateReplyReal(
      lastMsg,
      {
        systemPrompt: aiPrompt,
        productContext: aiContext,
        brandTone: tone,
        maxTokens: maxLength === 'short' ? 256 : maxLength === 'long' ? 1024 : 512,
      },
      {
        contactData: ctx.contact,
        orderData: getOrderContext(ctx.contact),
        conversationHistory,
        tone,
        contactId: ctx.contact?.id,
        conversationId: ctx.conversationId,
      },
    );

    // CRITICAL: Extract ONLY the reply text — never use the full JSON object
    let aiResponseText = '';
    if (typeof aiResult.reply === 'string') {
      aiResponseText = aiResult.reply.trim();
    }
    // Safety: if reply looks like JSON, try to extract the actual reply
    if (!aiResponseText || aiResponseText.startsWith('{') || aiResponseText.startsWith('[')) {
      try {
        const parsed = JSON.parse(aiResponseText);
        aiResponseText = (parsed?.reply || '').trim();
      } catch { /* not JSON, use as-is */ }
    }
    if (!aiResponseText || aiResponseText.startsWith('{')) {
      aiResponseText = `Thank you ${contactName}, we'll assist you shortly.`;
    }
    const trimmed = shortenAiResponse(aiResponseText, maxLength);

    if (saveField && !ctx.isTest) {
      contactService.update(ctx.contactId, { [saveField]: lastMsg });
      (ctx.contact as any)[saveField] = lastMsg;
    }

    let sentMessageId: string | undefined;
    let sendMode: 'api' | 'local' | 'suggestion' = autoSend ? 'local' : 'suggestion';

    if (autoSend && !ctx.isTest) {
      try {
        const { sendWhatsAppMessage } = await import('./whatsappService');
        const sendResult = await sendWhatsAppMessage(
          ctx.conversationId,
          ctx.contactId,
          trimmed,
          'automation',
          { automationId: ctx.automationId },
        );

        if (!sendResult.success) {
          logService.add('automations', 'automation_ai_reply_send_failed', `Automation AI reply send failed for ${contactName}`, {
            automationId: ctx.automationId,
            contactId: ctx.contactId,
            runId: ctx.runId,
            error: sendResult.error,
            aiResponse: trimmed,
          }, 'error', 'automation_run', ctx.runId);

          return {
            success: false,
            message: `AI reply send failed: ${sendResult.error || 'Unknown send error'}`,
            metadata: {
              aiResponse: trimmed,
              intent: aiResult.intent,
              confidence: aiResult.confidence,
              savedField: saveField,
            },
          };
        }

        sendMode = sendResult.mode;
        sentMessageId = sendResult.messageId || sendResult.localMessageId;
      } catch (sendErr: any) {
        // Do NOT silently fall back to local-only — propagate the real error
        logService.add('automations', 'automation_ai_reply_send_failed', `Automation AI reply send failed for ${contactName}: ${sendErr?.message}`, {
          automationId: ctx.automationId,
          contactId: ctx.contactId,
          runId: ctx.runId,
          error: sendErr?.message,
          aiResponse: trimmed,
        }, 'error', 'automation_run', ctx.runId);

        return {
          success: false,
          message: `AI reply send failed: ${sendErr?.message || 'Network error'}`,
          metadata: {
            aiResponse: trimmed,
            intent: aiResult.intent,
            confidence: aiResult.confidence,
            savedField: saveField,
          },
        };
      }

      // Update conversation in localStorage
      conversationService.update(ctx.conversationId, {
        lastMessageAt: new Date().toISOString(),
      });

      // Also persist to Supabase DB so the message is visible across sessions
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        if (sentMessageId) {
          await supabase.from('messages').upsert({
            id: sentMessageId,
            workspace_id: getCurrentWorkspaceId(),
            conversation_id: ctx.conversationId,
            contact_id: ctx.contactId,
            direction: 'outbound',
            message_type: 'text',
            body: trimmed,
            delivery_state: sendMode === 'api' ? 'sent' : 'pending',
            sent_by: 'ai',
            automation_id: ctx.automationId,
          });
          await supabase.from('conversations').update({
            last_message_preview: trimmed.substring(0, 100),
            last_message_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }).eq('id', ctx.conversationId);
          await supabase.from('contacts').update({
            last_message_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }).eq('id', ctx.contactId);
        }
      } catch (dbErr) {
        console.warn('[AutomationEngine] DB persist failed:', dbErr);
      }

      logService.add('automations', 'automation_ai_reply_sent', `Automation AI reply sent to ${contactName}`, {
        automationId: ctx.automationId,
        contactId: ctx.contactId,
        runId: ctx.runId,
        messageId: sentMessageId,
        sendMode,
        autoSend,
        aiResponse: trimmed,
        intent: aiResult.intent,
        confidence: aiResult.confidence,
        fallback: Boolean((aiResult as any)._fallback),
      }, 'success', 'automation_run', ctx.runId);
    }

    return {
      success: true,
      message: autoSend && !ctx.isTest ? `AI reply sent: ${trimmed}` : `AI reply generated: ${trimmed}`,
      metadata: {
        aiResponse: trimmed,
        intent: aiResult.intent,
        confidence: aiResult.confidence,
        savedField: saveField,
        sendMode,
        sentMessageId,
        fallback: Boolean((aiResult as any)._fallback),
      },
    };
  } catch (error: any) {
    return { success: false, message: `AI reply failed: ${error?.message || 'Unknown error'}` };
  }
}

// ===================== VALIDATION =====================

export function validateFlow(flow: AutomationFlow): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!flow.name?.trim()) errors.push('Automation name is required');

  const triggerNodes = flow.nodes.filter((node) => node.type === 'Trigger');
  if (triggerNodes.length === 0) errors.push('Must have exactly one trigger node');
  if (triggerNodes.length > 1) errors.push('Cannot have more than one trigger node');

  if (flow.nodes.length < 2) errors.push('Must have at least one action node after the trigger');

  if (flow.edges.length > 0) {
    const connectedNodeIds = new Set<string>();
    flow.edges.forEach((edge) => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });
    const disconnected = flow.nodes.filter((node) => !connectedNodeIds.has(node.id));
    if (disconnected.length > 0 && flow.nodes.length > 1) {
      errors.push(`Disconnected nodes: ${disconnected.map((node) => node.label).join(', ')}`);
    }
  }

  const nodeIds = new Set(flow.nodes.map((node) => node.id));
  (flow.edges || []).forEach((edge, index) => {
    if (!nodeIds.has(edge.source)) errors.push(`Edge ${index + 1}: source node "${edge.source}" does not exist`);
    if (!nodeIds.has(edge.target)) errors.push(`Edge ${index + 1}: target node "${edge.target}" does not exist`);
  });

  flow.nodes.forEach((node) => {
    if (node.type === 'Trigger') return;
    const actionType = node.config?.actionType || inferActionType(node);

    if (actionType === 'send_message' && !node.config?.message) {
      errors.push(`"${node.label}": message text is required`);
    }
    if (actionType === 'send_template' && !node.config?.templateId) {
      errors.push(`"${node.label}": template must be selected`);
    }
    if (actionType === 'send_template' && node.config?.templateId) {
      const template = templateService.getById(node.config.templateId);
      if (!template) errors.push(`"${node.label}": selected template not found`);
      else if (template.status !== 'approved') {
        errors.push(`"${node.label}": template "${template.name}" is ${template.status}, only approved templates can be sent`);
      }
    }
    if ((actionType === 'add_tag' || actionType === 'remove_tag') && !node.config?.tag) {
      errors.push(`"${node.label}": tag value is required`);
    }
    if (actionType === 'wait_delay' && (!node.config?.delayAmount || node.config.delayAmount <= 0)) {
      errors.push(`"${node.label}": delay amount must be positive`);
    }
    if (actionType === 'assign_agent' && !node.config?.agentId) {
      errors.push(`"${node.label}": agent must be selected`);
    }
    if (actionType === 'ai_reply' && !node.config?.aiPrompt?.trim()) {
      errors.push(`"${node.label}": AI instruction (prompt) is required`);
    }
    if (actionType === 'if_else_condition' && !node.config?.conditionField) {
      errors.push(`"${node.label}": condition field must be selected`);
    }
    if (actionType === 'if_else_condition' && node.config?.conditionField) {
      const operator = node.config?.conditionOperator || 'equals';
      if (operator !== 'exists' && operator !== 'not_exists' && !node.config?.conditionValue?.trim()) {
        errors.push(`"${node.label}": condition value is required for operator "${operator}"`);
      }
    }
    if (actionType === 'update_contact_field' && !node.config?.field) {
      errors.push(`"${node.label}": contact field must be selected`);
    }
    if (actionType === 'update_order_status' && !node.config?.orderStatus) {
      errors.push(`"${node.label}": order status must be selected`);
    }
    if (actionType === 'update_delivery_status' && !node.config?.deliveryStatus) {
      errors.push(`"${node.label}": delivery status must be selected`);
    }
    if (actionType === 'update_cod_confirmation' && !node.config?.codConfirmationStatus) {
      errors.push(`"${node.label}": COD confirmation status must be selected`);
    }
    if (actionType === 'update_conversation_status' && !node.config?.conversationStatus) {
      errors.push(`"${node.label}": conversation status must be selected`);
    }
    if (actionType === 'create_note' && !node.config?.noteBody?.trim()) {
      errors.push(`"${node.label}": note body is required`);
    }
    if (actionType === 'write_log' && !node.config?.logMessage?.trim()) {
      errors.push(`"${node.label}": log message is required`);
    }
  });

  return { valid: errors.length === 0, errors };
}
